import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { isApiError } from '@/lib/api/errors'
import { type NewDiscount, useCreateDiscount, useDiscounts } from './api'

/**
 * Create a discount code in Lemon Squeezy. With a customer, it's a personal
 * offer (one use by default) to send them; it works at checkout for a new
 * plan, an upgrade through checkout, or an add-on.
 */
export function DiscountDialog({
  open,
  onOpenChange,
  customer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer?: { id: string; name: string }
}) {
  const discounts = useDiscounts()
  const create = useCreateDiscount()
  const targets = discounts.data?.meta.targets ?? {}
  const [form, setForm] = useState({
    name: customer ? `Offer for ${customer.name}` : '',
    code: '',
    amount_type: 'percent' as NewDiscount['amount_type'],
    amount: '20',
    duration: 'once' as NewDiscount['duration'],
    duration_in_months: '3',
    applies_to: [] as string[],
    max_redemptions: customer ? '1' : '',
    expires_at: '',
  })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const err = (field: string) => errors[field]?.[0]

  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setErrors({})
    const amount = Number(form.amount)
    create.mutate(
      {
        name: form.name.trim(),
        code: form.code.trim(),
        amount_type: form.amount_type,
        // Fixed amounts are typed in dollars and sent in cents.
        amount: form.amount_type === 'fixed' ? Math.round(amount * 100) : Math.round(amount),
        duration: form.duration,
        duration_in_months: form.duration === 'repeating' ? Number(form.duration_in_months) : null,
        applies_to: form.applies_to,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
        workspace_id: customer?.id ?? null,
      },
      {
        onSuccess: (d) => {
          toast.success(`${d.code} is ready in Lemon Squeezy.`)
          onOpenChange(false)
        },
        onError: (error) => {
          // Field errors show under their fields; anything else (Lemon Squeezy refusing the
          // code, the connection) shows at the top of the form, so a click never looks ignored.
          const fields = isApiError(error) ? error.errors : {}
          if (Object.keys(fields).length > 0) setErrors(fields)
          else setErrors({ form: [error.message] })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        title={customer ? `A discount for ${customer.name}` : 'New discount code'}
        description="Created in Lemon Squeezy. Customers enter it at checkout: a new plan, an upgrade or an add-on. It can't be added to a plan they already pay for, and it can't be edited later (remove it and make a new one)."
      >
        <form onSubmit={submit} className="grid gap-4" noValidate>
          {err('form') && (
            <p
              role="alert"
              className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] text-danger"
            >
              {err('form')}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name (for the team)" htmlFor="d-name" required error={err('name')}>
              <Input id="d-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field
              label="Code customers type"
              htmlFor="d-code"
              required
              error={err('code')}
              hint="Letters and numbers only."
            >
              <Input
                id="d-code"
                value={form.code}
                onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="LOYAL20"
              />
            </Field>
            <Field label="Discount" htmlFor="d-amount" required error={err('amount')}>
              <div className="flex gap-2">
                <Input
                  id="d-amount"
                  type="number"
                  min={1}
                  value={form.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                  className="min-w-0"
                />
                <NativeSelect
                  aria-label="Discount type"
                  value={form.amount_type}
                  onChange={(e) => set({ amount_type: e.target.value as NewDiscount['amount_type'] })}
                  className="w-28"
                >
                  <option value="percent">% off</option>
                  <option value="fixed">USD off</option>
                </NativeSelect>
              </div>
            </Field>
            <Field
              label="For how long"
              htmlFor="d-duration"
              error={err('duration') ?? err('duration_in_months')}
            >
              <div className="flex gap-2">
                <NativeSelect
                  id="d-duration"
                  value={form.duration}
                  onChange={(e) => set({ duration: e.target.value as NewDiscount['duration'] })}
                >
                  <option value="once">First payment</option>
                  <option value="repeating">Some months</option>
                  <option value="forever">Every payment</option>
                </NativeSelect>
                {form.duration === 'repeating' && (
                  <Input
                    aria-label="Months"
                    type="number"
                    min={1}
                    max={36}
                    value={form.duration_in_months}
                    onChange={(e) => set({ duration_in_months: e.target.value })}
                    className="w-20"
                  />
                )}
              </div>
            </Field>
            <Field
              label="Times it can be used"
              htmlFor="d-max"
              error={err('max_redemptions')}
              hint="Empty for no limit."
            >
              <Input
                id="d-max"
                type="number"
                min={1}
                value={form.max_redemptions}
                onChange={(e) => set({ max_redemptions: e.target.value })}
              />
            </Field>
            <Field
              label="Expires"
              htmlFor="d-expires"
              error={err('expires_at')}
              hint="Empty to keep it running."
            >
              <Input
                id="d-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => set({ expires_at: e.target.value })}
              />
            </Field>
          </div>
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-[13px] font-medium text-ink">Works on</legend>
            <p className="text-xs text-ink-subtle">
              Leave everything unticked for every plan and add-on. Codes never work on lifetime plans.
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {Object.entries(targets).map(([key, name]) => (
                <label key={key} className="flex items-center gap-2 text-[13px] text-ink-muted">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-accent)]"
                    checked={form.applies_to.includes(key)}
                    onChange={(e) =>
                      set({
                        applies_to: e.target.checked
                          ? [...form.applies_to, key]
                          : form.applies_to.filter((k) => k !== key),
                      })
                    }
                  />
                  {name}
                </label>
              ))}
            </div>
            {err('applies_to') && <p className="text-xs text-danger">{err('applies_to')}</p>}
          </fieldset>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={form.code.length < 3 || form.name.trim() === ''}
            >
              Create code
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
